import { networkClient } from './client';
import type { ApiCollection, ApiEnvelope, TeamPageMatchdayStatus } from './types';

export interface BETeam {
  id: string;
  userId: string | null;
  tournamentCalendarId: string | null;
  teamName: string;
  managerName: string | null;
  budgetRemaining: number | null;
  transfersUsed: number | null;
  captainFantasyPlayerId: string | null;
  viceCaptainFantasyPlayerId: string | null;
  formation: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BETeamCurrentMatchday {
  id: string;
  matchdayNumber: number;
  status: 'upcoming' | 'active' | 'completed' | 'locked';
  startsAt: string | null;
  locksAt?: string | null;
  // Per-team lock for the current matchday (rules 5.6.2/5.6.3): true once one of this
  // team's players' fixtures has kicked off. For MD1 the matchday itself never goes
  // globally 'locked', so this is the authoritative per-manager lock signal.
  isLockedForTeam?: boolean;
}

export interface BETeamCurrentMatchdaySubstitution {
  id: string;
  matchdayId?: string;
  gameweekId?: string;
  subOutFantasyPlayerId: string;
  subInFantasyPlayerId: string;
  updatedFormation?: string | null;
  metadata?: BETeamSubstitutionMetadata | null;
  appliedAt: string;
}

export interface BETeamSubstitutionMetadata {
  lastFormation?: string | null;
  subOutPosition?: string | null;
  subInPosition?: string | null;
  subOutPositionSlot?: number | null;
  subInPositionSlot?: number | null;
  finalPlayersLayout?: TeamTransferPlayerLayoutInput[];
}

export interface BETeamGameweekSubstitution {
  id: string;
  gameweekId: string;
  subOutFantasyPlayerId: string;
  subInFantasyPlayerId: string;
  metadata?: BETeamSubstitutionMetadata | null;
  appliedAt: string;
}

export interface BETeamPlayer {
  id: string;
  fantasyPlayerId: string;
  isStarter: boolean;
  isCaptain: boolean;
  isViceCaptain: boolean;
  positionSlot: number | null;
  player?: BETeamPlayerDetail;
}

export interface BETeamPlayerDetail {
  id: string;
  firstName: string | null;
  lastName: string | null;
  shortFirstName: string | null;
  shortLastName: string | null;
  matchName: string | null;
  displayName: string;
  position: string | null;
  price: number;
  availabilityDisplay: string | null;
  isVisible: boolean;
  hasLeft: boolean;
  shirtKey: string | null;
  contestantFlagKey: string | null;
  contestantName: string | null;
  contestantShortName: string | null;
}

export type TeamTransferPlayerType = 'player' | 'captain' | 'viceCaptain';

export interface BETeamPendingTransferMetadata {
  playerType?: TeamTransferPlayerType;
}

export interface BETeamPendingTransfer {
  id: string;
  transferInId: string;
  transferOutId: string;
  gameweekId?: string | null;
  matchdayId: string | null;
  targetMatchdayId: string | null;
  updatedFormation: string | null;
  metadata?: BETeamPendingTransferMetadata | null;
  finalPlayersLayout?: TeamTransferPlayerLayoutInput[];
  transferInPlayer?: BETeamPlayerDetail;
  scheduledAt: string;
}

export type TeamCaptaincyRole = 'player' | 'captain' | 'viceCaptain';
export type TeamCaptaincyTargetRole = 'captain' | 'viceCaptain';

export interface BETeamPendingCaptaincyMetadata {
  lastCaptainFantasyPlayerId?: string | null;
  lastViceCaptainFantasyPlayerId?: string | null;
}

export interface BETeamPendingCaptaincyChange {
  id: string;
  fantasyPlayerId: string;
  gameweekId?: string | null;
  matchdayId: string | null;
  targetMatchdayId: string | null;
  fromRole: TeamCaptaincyRole;
  toRole: TeamCaptaincyTargetRole;
  metadata?: BETeamPendingCaptaincyMetadata | null;
  scheduledAt: string;
}

export interface BETeamManagedTransferSummary {
  periodType: 'matchday' | 'gameweek';
  periodId: string;
  mode: 'immediate' | 'pending';
  usedTransfers: number;
  totalTransfers: number;
}

export interface BETeamTransferHistoryPlayer {
  id: string;
  displayName: string;
  position: string | null;
  price: number;
  points: number;
  shirtKey: string | null;
  contestantFlagKey: string | null;
  contestantName: string | null;
  contestantShortName: string | null;
}

export interface BETeamTransferHistoryItem {
  id: string;
  appliedAt: string | null;
  transferIn: BETeamTransferHistoryPlayer;
  transferOut: BETeamTransferHistoryPlayer;
}

export interface BETeamTransferHistoryGameweek {
  gameweekId: string;
  gameweek: number;
  gameweekName: string | null;
  pointsDelta: number;
  transfers: BETeamTransferHistoryItem[];
}

export interface BETeamRankHistoryGameweek {
  gameweekId: string;
  gameweek: number;
  startsAt: string | null;
  endsAt: string | null;
  gameweekPoints: number;
  leaderboardRank: number;
}

export interface BETeamDetail extends BETeam {
  players: BETeamPlayer[];
  currentMatchdayPoints?: number;
  totalPoints?: number;
  gameweek?: number;
  gamweekStatus?: string;
  currentMatchday?: BETeamCurrentMatchday;
  currentMatchdaySubstitution?: BETeamCurrentMatchdaySubstitution;
  pendingTransfers?: BETeamPendingTransfer[];
  pendingCaptaincyChanges?: BETeamPendingCaptaincyChange[];
  hasUnlimitedTransfers?: boolean;
  managedTransferSummary?: BETeamManagedTransferSummary;
}

export interface BECreateTeamPlayerInput {
  fantasyPlayerId: string;
  isStarter?: boolean;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
  positionSlot?: number;
}

export interface CreateTeamPayload {
  userId: string;
  tournamentCalendarId: string;
  format?: 'classic_season_long' | 'knockout' | null;
  teamName: string;
  budgetRemaining?: number;
  formation?: string;
  captainFantasyPlayerId: string | null;
  viceCaptainFantasyPlayerId: string | null;
  players?: BECreateTeamPlayerInput[];
}

export interface BETeamScoringPlayer extends BETeamPlayer {
  totalPoints: number;
  gameweekPoints?: number;
  currentPeriodFixtures?: Array<{
    matchId: string;
    opponentId: string;
    opponentName: string;
    opponentShortName: string | null;
    contestantFlagKey: string | null;
    isHome: boolean;
    points: number | null;
    goalBonusNormalPoints: number | null;
    goals?: number | null;
    // True when the player has no stats for this fixture (empty or all-zero
    // breakdown). Distinguishes "hasn't featured" from a real net-zero score.
    showDNP?: boolean;
    gameweek?: number;
    status: string;
    kickoffAt: string;
    matchday?: number;
  }>;
}

export interface BETeamScoringDetail extends Omit<BETeam, 'createdAt' | 'updatedAt'> {
  players: BETeamScoringPlayer[];
  totalPoints: number;
  gameweek?: number;
  gamweekStatus?: string;
  gameweekPoints?: number;
  // Player gameweekPoints above are EFFECTIVE (captain/vice doubling and the
  // active booster already applied by the backend), so they sum to
  // gameweekPoints. These describe how that happened.
  effectiveCaptainFantasyPlayerId?: string | null;
  activeBoosterCode?: string | null;
  twelfthMan?: { fantasyPlayerId: string; gameweekPoints: number } | null;
}

export type CreateTeamResponse = ApiEnvelope<BETeam>;
export type TeamDetailResponse = ApiEnvelope<BETeamDetail>;
export type TeamsListResponse = ApiEnvelope<ApiCollection<BETeamDetail>>;
export type TeamTransferHistoryResponse = ApiEnvelope<{
  items: BETeamTransferHistoryGameweek[];
}>;
export type TeamRankHistoryResponse = ApiEnvelope<{
  items: BETeamRankHistoryGameweek[];
}>;
export type TeamScoringResponse = ApiEnvelope<BETeamScoringDetail>;

export interface FetchTeamsFilters {
  userId?: string;
  tournamentCalendarId?: string;
}

export interface TeamTransferItemInput {
  outFantasyPlayerId: string;
  inFantasyPlayerId: string;
}

export interface TeamTransferPlayerLayoutInput {
  fantasyPlayerId: string;
  positionSlot: number;
}

export interface TransferPlayersPayload {
  teamId: string;
  transfers: TeamTransferItemInput[];
  format?: 'classic_season_long' | 'knockout' | null;
  matchdayId?: string;
  gameweekId?: string;
  transferMode: 'immediate' | 'pending';
  formation?: string;
  players?: TeamTransferPlayerLayoutInput[];
}

export interface UpdateTeamCaptaincyPayload {
  teamId: string;
  role: 'captain' | 'viceCaptain';
  fantasyPlayerId: string;
}

export interface QueuePendingCaptaincyPayload {
  teamId: string;
  role: 'captain' | 'viceCaptain';
  fantasyPlayerId: string;
  format?: 'classic_season_long' | 'knockout' | null;
  matchdayId?: string;
  gameweekId?: string;
}

export interface RevertPendingTransfersPayload {
  teamId: string;
}

export interface UpdateTeamPayload {
  teamId: string;
  teamName: string;
}

export interface RevertPendingCaptaincyPayload {
  teamId: string;
}

export interface RevertPendingTransfersResult {
  revertedTransfers: number;
  revertedTransferIds: string[];
}

export interface RevertPendingTransfersResponse extends ApiEnvelope<RevertPendingTransfersResult> {
  message?: string;
}

export interface RevertPendingCaptaincyResult {
  revertedCaptaincyChanges: number;
  revertedCaptaincyChangeIds: string[];
}

export interface RevertPendingCaptaincyResponse extends ApiEnvelope<RevertPendingCaptaincyResult> {
  message?: string;
}

export const createTeam = (payload: CreateTeamPayload): Promise<CreateTeamResponse> => {
  return networkClient.post<CreateTeamResponse>('/api/teams', payload).then(({ data }) => data);
};

export const fetchTeam = (teamId: string): Promise<TeamDetailResponse> => {
  return networkClient.get<TeamDetailResponse>(`/api/teams/${teamId}`).then(({ data }) => data);
};

export const fetchTeamScoring = (
  teamId: string,
  gameweekId: string,
  includeGameweekFixtures?: boolean
): Promise<TeamScoringResponse> => {
  return networkClient
    .get<TeamScoringResponse>(`/api/teams/scoring/${teamId}`, {
      params: {
        gameweek_id: gameweekId,
        ...(includeGameweekFixtures ? { include_gameweek_fixtures: true } : {}),
      },
    })
    .then(({ data }) => data);
};

export interface GetPastFantasyTeamParams {
  userId: string;
  tournamentCalendarId: string;
  gameweek_id: string;
}

export const getPastFantasyTeam = ({
  userId,
  tournamentCalendarId,
  gameweek_id,
}: GetPastFantasyTeamParams): Promise<TeamHistoryResponse> => {
  return networkClient
    .get<TeamHistoryResponse>(`/api/teams/history`, {
      params: {
        userId,
        tournamentCalendarId,
        gameweek_id,
      },
    })
    .then(({ data }) => data);
};

export const fetchTeams = (filters: FetchTeamsFilters): Promise<TeamsListResponse> => {
  return networkClient
    .get<TeamsListResponse>('/api/teams', {
      params: filters,
    })
    .then(({ data }) => data);
};

export const fetchTeamTransferHistory = (teamId: string): Promise<TeamTransferHistoryResponse> => {
  return networkClient
    .get<TeamTransferHistoryResponse>(`/api/teams/${teamId}/transfer-history`)
    .then(({ data }) => data);
};

export const fetchTeamRankHistory = (teamId: string): Promise<TeamRankHistoryResponse> => {
  return networkClient
    .get<TeamRankHistoryResponse>(`/api/teams/${teamId}/rank-history`)
    .then(({ data }) => data);
};

export const updateTeam = ({
  teamId,
  teamName,
}: UpdateTeamPayload): Promise<TeamDetailResponse> => {
  return networkClient
    .patch<TeamDetailResponse>(`/api/teams/${teamId}`, { teamName })
    .then(({ data }) => data);
};

export const updateTeamCaptaincy = ({
  teamId,
  role,
  fantasyPlayerId,
}: UpdateTeamCaptaincyPayload): Promise<TeamDetailResponse> => {
  return networkClient
    .patch<TeamDetailResponse>(`/api/teams/${teamId}/captaincy`, {
      role,
      fantasyPlayerId,
    })
    .then(({ data }) => data);
};

export const queuePendingCaptaincy = ({
  teamId,
  role,
  fantasyPlayerId,
  format,
  matchdayId,
  gameweekId,
}: QueuePendingCaptaincyPayload): Promise<TeamDetailResponse> => {
  return networkClient
    .post<TeamDetailResponse>(`/api/teams/${teamId}/pending-captaincy`, {
      role,
      fantasyPlayerId,
      format,
      matchdayId,
      gameweekId,
    })
    .then(({ data }) => data);
};

export const transferPlayers = ({
  teamId,
  transfers,
  format,
  matchdayId,
  gameweekId,
  transferMode,
  formation,
  players,
}: TransferPlayersPayload): Promise<TeamDetailResponse> => {
  return networkClient
    .post<TeamDetailResponse>(`/api/teams/${teamId}/transfers`, {
      format,
      transfers,
      matchdayId,
      gameweekId,
      transferMode,
      formation,
      players,
    })
    .then(({ data }) => data);
};

export const revertPendingTransfers = ({
  teamId,
}: RevertPendingTransfersPayload): Promise<RevertPendingTransfersResponse> => {
  return networkClient
    .post<RevertPendingTransfersResponse>(`/api/teams/${teamId}/pending-transfers/revert`)
    .then(({ data }) => data);
};

export const revertPendingCaptaincy = ({
  teamId,
}: RevertPendingCaptaincyPayload): Promise<RevertPendingCaptaincyResponse> => {
  return networkClient
    .post<RevertPendingCaptaincyResponse>(`/api/teams/${teamId}/pending-captaincy/revert`)
    .then(({ data }) => data);
};

export interface TeamHistoryItem extends BETeamScoringDetail {
  gameweekId: string;
  gameweekNumber: number;
  gameweekName: string;
  rank?: number;
  gameweekSubstitution?: BETeamGameweekSubstitution;
}

export type TeamHistoryResponse = ApiEnvelope<{
  items: TeamHistoryItem[];
}>;

export interface AvailableGameweek {
  id: string;
  number: number;
  name: string;
  status: string;
  startsAt?: string;
  endsAt?: string;
  isCompleted?: boolean;
}

export type AvailableGameweeksResponse = ApiEnvelope<{
  gameweeks: {
    id: string;
    name: string;
    number: number;
    status: TeamPageMatchdayStatus | 'finalised';
    startsAt: string;
    endsAt?: string;
    isCompleted?: boolean;
  }[];
}>;

interface FetchAvailableGameweeksProps {
  tournamentCalendarId: string;
  teamId?: string;
  includeLockedLive?: boolean;
}

export const fetchAvailableGameweeks = ({
  tournamentCalendarId,
  teamId,
  includeLockedLive,
}: FetchAvailableGameweeksProps): Promise<AvailableGameweeksResponse> => {
  return networkClient
    .get<AvailableGameweeksResponse>('/api/fantasy-teams/available-gameweeks', {
      params: {
        tournament_calendar: tournamentCalendarId,
        team_id: teamId,
        include_locked_live: includeLockedLive,
      },
    })
    .then(({ data }) => data);
};
